import { captureException } from "@sentry/react";
import { useCallback, useMemo, useState } from "react";
import Cancel from "~icons/material-symbols/cancel-outline";
import CheckCircle from "~icons/material-symbols/check-circle-outline";
import { css } from "../../../../styled-system/css";
import { LoadingIcon } from "../../../components/loadingIcon";
import { t } from "../../../libs/i18n";
import { formatNumber } from "../../../libs/utils";

type TransferAcceptedModalProps = {
  data: TransferAcceptedModalStateData;
  onAccept: () => Promise<void>;
  onClose: () => void;
};

export function TransferInInitiatedModal({
  data: { amount },
  onAccept,
  onClose,
}: TransferAcceptedModalProps) {
  const [isAccepted, setIsAccepted] = useState(false);
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleAccept = useCallback(async () => {
    setIsLoading(true);

    try {
      await onAccept();
      setIsAccepted(true);
    } catch (error) {
      captureException(error);
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  }, [onAccept]);

  const ErrorComponent = useMemo(() => {
    return (
      <div
        className={css({
          backgroundColor: "white",
          borderRadius: "16px",
          padding: "24px",
          width: "80%",
          maxWidth: "360px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        })}
      >
        <h2
          className={css({
            fontSize: "18px",
            fontWeight: "bold",
            marginBottom: "24px",
          })}
        >
          {t("受け取りコードが利用できません")}
        </h2>

        <div
          className={css({
            borderRadius: "8px",
            marginBottom: "24px",
          })}
        >
          <div
            className={css({
              fontSize: "56px",
              fontWeight: "bold",
              color: "text.accentError",
            })}
          >
            <Cancel />
          </div>
        </div>

        <button
          type="button"
          className={css({
            padding: "12px",
            borderRadius: "8px",
            width: "100%",
            fontWeight: "bold",
            cursor: "pointer",
            backgroundColor: "surface.secondary",
            color: "text.primary",
            border: "1px solid",
            borderColor: "border.secondary",
          })}
          onClick={onClose}
        >
          {t("閉じる")}
        </button>
      </div>
    );
  }, [onClose]);

  const Accepted = useMemo(() => {
    return (
      <div
        className={css({
          backgroundColor: "white",
          borderRadius: "16px",
          padding: "24px",
          width: "80%",
          maxWidth: "360px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        })}
      >
        <h2
          className={css({
            fontSize: "18px",
            fontWeight: "bold",
            marginBottom: "24px",
          })}
        >
          {t("ポイント受け取り完了")}
        </h2>

        <div
          className={css({
            borderRadius: "8px",
            marginBottom: "24px",
          })}
        >
          <div
            className={css({
              fontSize: "56px",
              fontWeight: "bold",
              color: "rgb(0, 150, 100)",
            })}
          >
            <CheckCircle />
          </div>
        </div>

        <button
          type="button"
          className={css({
            padding: "12px",
            borderRadius: "8px",
            width: "100%",
            fontWeight: "bold",
            cursor: "pointer",
            backgroundColor: "surface.secondary",
            color: "text.primary",
            border: "1px solid",
            borderColor: "border.secondary",
          })}
          onClick={onClose}
        >
          {t("閉じる")}
        </button>
      </div>
    );
  }, [onClose]);

  return (
    <div
      className={css({
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(226, 226, 226, 0.5)",
        backdropFilter: "blur(4px)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 1000,
      })}
    >
      {isError ? (
        ErrorComponent
      ) : isAccepted ? (
        Accepted
      ) : (
        <div
          className={css({
            backgroundColor: "white",
            borderRadius: "16px",
            padding: "24px",
            width: "80%",
            maxWidth: "360px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          })}
        >
          <h2
            className={css({
              fontSize: "18px",
              fontWeight: "bold",
              marginBottom: "24px",
            })}
          >
            {t("ポイントを受け取りますか？")}
          </h2>

          <div
            className={css({
              backgroundColor: "rgb(239, 250, 244)",
              padding: "16px 32px",
              borderRadius: "8px",
              marginBottom: "24px",
            })}
          >
            <div
              className={css({
                fontSize: "24px",
                fontWeight: "bold",
                color: "rgb(0, 150, 100)",
              })}
            >
              {formatNumber(amount)} pt
            </div>
          </div>

          <button
            type="button"
            className={css({
              backgroundColor: isLoading
                ? "surface.secondary"
                : "text.accentPrimary",
              color: "white",
              padding: "12px",
              borderRadius: "8px",
              width: "100%",
              fontWeight: "bold",
              cursor: "pointer",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            })}
            onClick={handleAccept}
            disabled={isLoading}
          >
            {isLoading ? <LoadingIcon /> : t("受け取る")}
          </button>

          <button
            type="button"
            className={css({
              padding: "12px",
              borderRadius: "8px",
              width: "100%",
              fontWeight: "bold",
              cursor: "pointer",
              opacity: isLoading ? 0.5 : 1,
            })}
            disabled={isLoading}
            onClick={onClose}
          >
            {t("キャンセル")}
          </button>
        </div>
      )}
    </div>
  );
}
